import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../models/api-response';
import {
  AttributeDto,
  AttributeOperatorViewWithOperatorAttributeSettingsDto,
  OperatorAttributeDto,
  OperatorAttributeSettingsDto,
  OperatorAttributeSettingsWrite,
} from '../models/attribute-dto';
import { PaginatedData } from '../models/paginated-data';

@Injectable({ providedIn: 'root' })
export class AttributeService {
  constructor(private httpClient: HttpClient) {}

  getAttributeById(attributeId: number): Observable<AttributeDto> {
    return this.httpClient.get<AttributeDto>(`${environment.backendUrl}/api/v1/catalogue/attributes/${attributeId}`);
  }

  getAttributesByCategoryId(categoryId: number): Observable<AttributeDto[]> {
    return this.httpClient.get<AttributeDto[]>(
      `${environment.backendUrl}/api/v1/catalogue/attributes/category/${categoryId}`,
    );
  }

  readByName(attributeName: string): Observable<AttributeDto> {
    return this.httpClient.get<AttributeDto>(
      `${environment.backendUrl}/api/v1/catalogue/attributes/attribute/name/${attributeName}`,
    );
  }

  getSelectedAttributesByCatalogueId(catalogueId: number): Observable<AttributeDto[]> {
    return this.httpClient.get<AttributeDto[]>(
      `${environment.backendUrl}/api/admin/v1/catalogue/attributes/part-editor-selection/catalogue/${catalogueId}`,
    );
  }

  upsetAttrPartSelection(catalogueId: number, selectedIds: number[]): Observable<any> {
    return this.httpClient.put<ApiResponse>(
      `${environment.backendUrl}/api/v1/catalogue/attribute-part-editor-selection/catalogue/${catalogueId}/bulk`,
      { attributeIds: selectedIds },
    );
  }

  get(pageNumber?: number, pageSize?: number): Observable<PaginatedData<AttributeDto>> {
    let params = new HttpParams();
    if (pageNumber) {
      params = params.append('pageNumber', pageNumber.toString());
    }
    if (pageSize) {
      params = params.append('pageSize', pageSize.toString());
    }
    return this.httpClient
      .get(`${environment.backendUrl}/api/admin/v1/catalogue/attributes`, {
        params,
      })
      .pipe(map((response: PaginatedData<AttributeDto>) => response));
  }

  getOperatorAttributes(): Observable<OperatorAttributeDto[]> {
    return this.httpClient.get<OperatorAttributeDto[]>(
      `${environment.backendUrl}/api/operator/v1/attribute-operator-views`,
    );
  }

  getOperatorAttributesSettings(operatorId: number): Observable<OperatorAttributeSettingsDto[]> {
    return this.httpClient.get<OperatorAttributeSettingsDto[]>(
      `${environment.backendUrl}/api/admin/v1/operator-attribute-settings/operator/${operatorId}`,
    );
  }

  updateOperatorAttributesSettings(data: OperatorAttributeSettingsWrite[]): Observable<any> {
    return this.httpClient.put<ApiResponse>(
      `${environment.backendUrl}/api/admin/v1/operator-attribute-settings/bulk`,
      data,
    );
  }

  getOperatorAttributesByCatalogueId(
    catalogueId: number,
  ): Observable<AttributeOperatorViewWithOperatorAttributeSettingsDto[]> {
    return this.httpClient.get<AttributeOperatorViewWithOperatorAttributeSettingsDto[]>(
      `${environment.backendUrl}/api/operator/v1/attribute-operator-views-with-opeartor-attribute-settings/npc-selection/catalogue/${catalogueId}`,
    );
  }

  getOperatorSelectionAttr(catalogueId: number): Observable<AttributeOperatorViewWithOperatorAttributeSettingsDto[]> {
    return this.httpClient.get<AttributeOperatorViewWithOperatorAttributeSettingsDto[]>(
      `${environment.backendUrl}/api/operator/v1/attribute-operator-views-with-opeartor-attribute-settings/part-editor-selection/catalogue/${catalogueId}`,
    );
  }

  delete(attributeId: number): Observable<any> {
    return this.httpClient.delete<ApiResponse>(`${environment.backendUrl}/api/v1/catalogue/attributes/${attributeId}`);
  }

  update(attributeId: number, attribute: AttributeDto): Observable<any> {
    return this.httpClient.put<ApiResponse>(
      `${environment.backendUrl}/api/v1/catalogue/attributes/${attributeId}`,
      attribute,
    );
  }

  create(attribute: AttributeDto): Observable<any> {
    return this.httpClient.post<ApiResponse>(`${environment.backendUrl}/api/v1/catalogue/attributes`, attribute);
  }
}
